
import React, { useState, useEffect, useCallback } from "react";
import { Eye, Trash2, PlayCircle, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { getDefaultThumbnail } from "./utils/thumbnailExtractor";
import { supabase } from "@/integrations/supabase/client";
import { toastService } from "@/utils/toastService";

interface MediaItemProps {
  mediaUrl: string;
  mediaType: 'image' | 'video' | 'audio'; // Use explicit media type
  index: number;
  onView: (mediaUrl: string) => void;
  onRemove?: (index: number) => void;
  disabled?: boolean;
  thumbnailUrl?: string;
}

export const MediaItem = ({ 
  mediaUrl, 
  mediaType, 
  index, 
  onView, 
  onRemove, 
  disabled = false, 
  thumbnailUrl 
}: MediaItemProps) => {
  const isVideo = mediaType === 'video';
  const isAudio = mediaType === 'audio';
  const isMediaFile = isVideo || isAudio;
  const isMobile = useIsMobile();
  const [posterImage, setPosterImage] = useState<string>("/placeholder.svg");
  const [thumbnailError, setThumbnailError] = useState(false);
  
  // Convert storage path to public URL if needed (fallback for legacy data)
  const getPublicUrl = useCallback((path: string): string => {
    if (!path) return "";
    
    // If it already looks like a full URL, return it (new format)
    if (path.startsWith('http') || path.startsWith('blob:')) return path;
    
    // Check if it's a storage path that needs conversion
    if (path.startsWith('stories/') || path.startsWith('life-stages/') || path.startsWith('gallery/')) {
      try {
        const { data: { publicUrl } } = supabase.storage
          .from('memorials')
          .getPublicUrl(path);
          
        return publicUrl;
      } catch (error) {
        console.error(`[MediaItem][${index}] Error getting public URL:`, error);
        return "";
      }
    }
    
    // If it doesn't match known patterns, return as-is
    return path;
  }, [index]);

  // Use an effect to set the optimal thumbnail source
  useEffect(() => {
    // Use server thumbnail if provided (most reliable option)
    if (thumbnailUrl) {
      setPosterImage(getPublicUrl(thumbnailUrl));
      setThumbnailError(false);
      return;
    }

    // If it's a video and no thumbnail provided, use a default video thumbnail
    if (isVideo) {
      const defaultThumb = getDefaultThumbnail(mediaUrl);
      setPosterImage(defaultThumb);
    }
  }, [isVideo, mediaUrl, thumbnailUrl, index, getPublicUrl]);

  const handlePlayClick = () => {
    onView(getPublicUrl(mediaUrl));
  };

  return (
    <div className="w-full h-full rounded-lg overflow-hidden group relative bg-gray-100">
      {isVideo ? (
        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
          <img 
            src={posterImage}
            alt="Video thumbnail"
            className="w-full h-full object-cover"
            onError={(e) => {
              if (!thumbnailError) {
                console.error(`[MediaItem][${index}] Error loading thumbnail:`, posterImage);
                setPosterImage(getDefaultThumbnail(mediaUrl));
                setThumbnailError(true);
              } else {
                e.currentTarget.src = "/placeholder.svg";
              }
            }}
          />
        </div>
      ) : isAudio ? (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
          <Volume2 className="h-12 w-12 text-gray-500" />
        </div>
      ) : (
        <img
          src={getPublicUrl(mediaUrl)}
          alt={`Gallery item ${index + 1}`}
          className="w-full h-full object-cover"
          onError={(e) => {
            console.error(`[MediaItem][${index}] Image error loading:`, {
              originalSrc: mediaUrl,
              publicUrl: getPublicUrl(mediaUrl),
              error: e
            });
            e.currentTarget.src = "/placeholder.svg";
          }}
        />
      )}
      
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/60">
        {isMediaFile ? (
          // Show both Play and Delete buttons for video/audio (stacked vertically)
          <div className="flex flex-col gap-2 w-3/4">
            <Button
              variant="outline"
              size={isMobile ? "sm" : "sm"}
              className="bg-green-500 hover:bg-green-600 text-white border-0"
              onClick={handlePlayClick}
              disabled={disabled}
            >
              {isVideo ? (
                <PlayCircle className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} ${isMobile ? "mr-1" : "mr-2"}`} />
              ) : (
                <Volume2 className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} ${isMobile ? "mr-1" : "mr-2"}`} />
              )}
              Play
            </Button>
            {onRemove && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size={isMobile ? "sm" : "sm"}
                    className="bg-red-500 hover:bg-red-600 text-white border-0"
                    disabled={disabled}
                  >
                    <Trash2 className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} ${isMobile ? "mr-1" : "mr-2"}`} />
                    Remove
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove this media?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This {isVideo ? "video" : "audio"} will be permanently deleted.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onRemove(index)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        ) : (
          // Show View and Delete buttons for images (stacked vertically like video/audio)
          <div className="flex flex-col gap-2 w-3/4">
            <Button
              variant="outline"
              size={isMobile ? "sm" : "sm"}
              className="bg-green-500 hover:bg-green-600 text-white border-0"
              onClick={() => onView(getPublicUrl(mediaUrl))}
              disabled={disabled}
            >
              <Eye className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} ${isMobile ? "mr-1" : "mr-2"}`} />
              View
            </Button>
            {onRemove && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size={isMobile ? "sm" : "sm"}
                    className="bg-red-500 hover:bg-red-600 text-white border-0"
                    disabled={disabled}
                  >
                    <Trash2 className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} ${isMobile ? "mr-1" : "mr-2"}`} />
                    Remove
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove this media?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This image will be permanently deleted.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onRemove(index)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
